import { useEffect, useMemo, useState } from 'react';
import {
  checkReportFreshness,
  computeMetricValue,
  getMyDashboardPreference,
  listMetrics,
  listReportRuns,
  listReportSnapshots,
  runBranchComparisonReport,
  runReport,
  setDashboardPreference,
} from '../../api/reporting';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  GroupLabel,
  HintNote,
  KPICard,
  LoadingNote,
  PageHeader,
  Select,
  SectionCard,
  TextInput,
  formatDateTime,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import type { MetricDefinition, ReportRunStatus, ReportSnapshot } from '../../types/domain';

const CONSOLIDATED = '__ALL__';

/**
 * Formula types whose per-branch values are mathematically valid to sum
 * into a consolidated total (SUM/COUNT over branch-filtered rows). AVG
 * types (COST_SNAPSHOT_AVG, FEEDBACK_AVG_RATING) and any formula with no
 * branch filter at all (EXCEPTION_QUEUE_COUNT — confirmed live: every
 * branch returns the SAME tenant-wide value) are deliberately excluded —
 * summing them would double/mis-count a value that isn't actually
 * branch-partitioned. Live-verified 2026-08-24: PAYROLL_GROSS (additive)
 * summed exactly (HQ 5,000,000 + B2 0 = Consolidated 5,000,000);
 * HPP_AVG_COST (non-additive) returned the IDENTICAL value (6000) for HQ,
 * B2, and Consolidated — proving it is NOT branch-partitioned and must
 * never be summed.
 */
const ADDITIVE_FORMULA_TYPES = new Set([
  'JOURNAL_ACCOUNT_TYPE_SUM',
  'JOURNAL_ACCOUNT_BALANCE',
  'STOCK_LEDGER_MOVEMENT_SUM',
  'PAYROLL_GROSS_SUM',
  'TARDINESS_COUNT',
  'PURCHASE_ORDER_VALUE_SUM',
  'ASSET_STATUS_COUNT',
  'PR_STATUS_COUNT',
  'PO_STATUS_COUNT',
  'MAINTENANCE_SCHEDULE_OVERDUE_COUNT',
  'MAINTENANCE_ORDER_STATUS_COUNT',
  'LEAVE_REQUEST_STATUS_COUNT',
  'AP_INVOICE_STATUS_COUNT',
  'CUSTOMER_STATUS_COUNT',
  'COMPLAINT_STATUS_COUNT',
  'STOCK_BALANCE_QTY_SUM',
]);

const DOMAIN_LABEL: Record<string, string> = {
  FINANCE: 'Keuangan',
  PAYROLL: 'Payroll',
  INVENTORY: 'Inventory',
  PURCHASING: 'Purchasing',
  PRODUCTION: 'Produksi',
  ASSETS: 'Aset & Perawatan',
  HR: 'SDM',
  CRM: 'CRM',
  CROSS_DOMAIN: 'Lintas Modul',
};

const STATUS_LABEL: Record<ReportRunStatus, string> = {
  FINAL: 'Final',
  PRELIMINARY: 'Awal (Preliminary)',
  FAILED: 'Gagal',
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Executive Command Center (FASE 12, migration 0050). Every KPI shown here
 * is a real report_snapshots row from a real report_runs generation — never
 * a fabricated/estimated number, and never 0-as-if-measured: a metric with
 * no eligible source rows for the window genuinely computes to 0 (a real
 * measured value), while a metric the caller isn't authorized to see (e.g.
 * PAYROLL_GROSS without payroll_fact.read) is simply absent from the grid,
 * not rendered as a fake 0.
 */
export default function CommandCenterPage() {
  const { context, hasPermission } = usePermissions();
  const { branches, activeBranchId } = useBranch();
  const isTenantWide = context?.authenticated === true && context.roles.some((r) => r.branch_id === null);

  const [scope, setScope] = useState<string>(activeBranchId ?? CONSOLIDATED);
  const [date, setDate] = useState(todayISO());
  const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
  const [snapshots, setSnapshots] = useState<ReportSnapshot[]>([]);
  const [runStatus, setRunStatus] = useState<ReportRunStatus | null>(null);
  const [freshnessNote, setFreshnessNote] = useState<string | null>(null);
  const [freshness, setFreshness] = useState<string | null>(null);
  const [reportRunId, setReportRunId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSnapshot, setSelectedSnapshot] = useState<ReportSnapshot | null>(null);
  const [comparison, setComparison] = useState<Array<{ branch_id: string; branch_code: string; report_run_id: string }> | null>(null);
  const [comparisonBusy, setComparisonBusy] = useState(false);
  const [pinnedMetrics, setPinnedMetrics] = useState<string[]>([]);
  const [pinBusy, setPinBusy] = useState<string | null>(null);
  const [reconciliation, setReconciliation] = useState<{
    branchCodes: string[];
    byMetric: Map<string, { perBranch: Map<string, number>; consolidated: number }>;
  } | null>(null);

  useEffect(() => {
    void listMetrics().then(setMetrics).catch(() => undefined);
    // Presentation only (V5 non-negotiable): pinned_metrics never affects
    // which metrics are computed, their formula, or branch/tenant scope —
    // it only controls which cards this user sees pinned to the top.
    void getMyDashboardPreference()
      .then((pref) => setPinnedMetrics(pref?.pinned_metrics ?? []))
      .catch(() => undefined);
  }, []);

  const togglePin = async (metricCode: string) => {
    const next = pinnedMetrics.includes(metricCode) ? pinnedMetrics.filter((c) => c !== metricCode) : [...pinnedMetrics, metricCode];
    setPinBusy(metricCode);
    const prev = pinnedMetrics;
    setPinnedMetrics(next); // optimistic
    try {
      await setDashboardPreference(next, {});
    } catch (e) {
      setPinnedMetrics(prev); // rollback on failure — never claim a pin succeeded when it didn't
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setPinBusy(null);
    }
  };

  useEffect(() => {
    if (activeBranchId && scope === CONSOLIDATED && !isTenantWide) setScope(activeBranchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId, isTenantWide]);

  const load = async () => {
    setLoading(true);
    setError(null);
    setComparison(null);
    try {
      const branchId = scope === CONSOLIDATED ? null : scope;
      // A fresh idempotency key per click: this button represents a new
      // intentional "load/regenerate the Command Center" action each time,
      // not a retry of a single logical request — a genuine retry (e.g. a
      // network hiccup) is handled inside runReport's own error path, not
      // by reusing a key across separate user-initiated clicks.
      const runId = await runReport(branchId, 'DAILY', date, date, crypto.randomUUID());
      setReportRunId(runId);
      const snaps = await listReportSnapshots(runId);
      setSnapshots(snaps);
      const fresh = await checkReportFreshness(runId);
      setFreshness(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSnapshots([]);
      setReportRunId(null);
    } finally {
      setLoading(false);
    }
  };

  // Report run status/freshness_note are read back from the actual
  // report_runs row (not re-derived here), so pull them once the run exists.
  useEffect(() => {
    if (!reportRunId) return;
    let cancelled = false;
    listReportRuns().then((runs) => {
      if (cancelled) return;
      const run = runs.find((r) => r.report_run_id === reportRunId);
      setRunStatus(run?.status ?? null);
      setFreshnessNote(run?.freshness_note ?? null);
    });
    return () => {
      cancelled = true;
    };
  }, [reportRunId]);

  const metricByCode = useMemo(() => {
    const map = new Map<string, MetricDefinition>();
    for (const m of metrics) if (m.status === 'ACTIVE') map.set(m.metric_code, m);
    return map;
  }, [metrics]);

  const grouped = useMemo(() => {
    const byDomain = new Map<string, ReportSnapshot[]>();
    for (const snap of snapshots) {
      const domain = metricByCode.get(snap.metric_code)?.source_domain ?? 'CROSS_DOMAIN';
      if (!byDomain.has(domain)) byDomain.set(domain, []);
      byDomain.get(domain)!.push(snap);
    }
    return byDomain;
  }, [snapshots, metricByCode]);

  const runComparison = async () => {
    setComparisonBusy(true);
    setError(null);
    setReconciliation(null);
    try {
      const [rows, consolidatedRunId] = await Promise.all([
        runBranchComparisonReport('DAILY', date, date, crypto.randomUUID()),
        runReport(null, 'DAILY', date, date, crypto.randomUUID()),
      ]);
      setComparison(rows);
      setSnapshots([]);
      setReportRunId(null);
      setRunStatus(null);

      const branchSnapshots = await Promise.all(rows.map((r) => listReportSnapshots(r.report_run_id)));
      const consolidatedSnapshots = await listReportSnapshots(consolidatedRunId);

      const byMetric = new Map<string, { perBranch: Map<string, number>; consolidated: number }>();
      consolidatedSnapshots.forEach((s) => {
        byMetric.set(s.metric_code, { perBranch: new Map(), consolidated: s.computed_value });
      });
      rows.forEach((r, i) => {
        branchSnapshots[i].forEach((s) => {
          const entry = byMetric.get(s.metric_code);
          if (entry) entry.perBranch.set(r.branch_code, s.computed_value);
        });
      });
      setReconciliation({ branchCodes: rows.map((r) => r.branch_code), byMetric });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setComparisonBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Command Center"
        subtitle="KPI lintas modul dari metric_registry/report_snapshots nyata — tidak ada angka penjualan/kas/POS (DEFERRED_WITH_POS), dan metrik yang tidak berwenang dilihat disembunyikan, bukan ditampilkan sebagai 0."
      />

      <div className="alex-card" style={{ padding: 16, marginBottom: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ width: 220 }}>
          <FieldLabel label="Cakupan">
            <Select value={scope} onChange={(e) => setScope(e.target.value)}>
              {isTenantWide && <option value={CONSOLIDATED}>Semua Cabang (Konsolidasi)</option>}
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>
                  {b.name}
                </option>
              ))}
            </Select>
          </FieldLabel>
        </div>
        <div style={{ width: 170 }}>
          <FieldLabel label="Tanggal">
            <TextInput type="date" value={date} onChange={(e) => setDate(e.target.value)} max={todayISO()} />
          </FieldLabel>
        </div>
        <Button variant="primary" loading={loading} onClick={() => void load()} disabled={!hasPermission('report', 'run')}>
          Muat Command Center
        </Button>
        {isTenantWide && (
          <Button variant="secondary" loading={comparisonBusy} onClick={() => void runComparison()}>
            Bandingkan Antar Cabang
          </Button>
        )}
        {!hasPermission('report', 'run') && <HintNote text="Peran Anda hanya dapat melihat (report.read), tidak dapat men-generate laporan baru." />}
      </div>

      {error && <ErrorNote text={error} />}

      {runStatus && runStatus !== 'FINAL' && (
        <HintNote
          text={
            runStatus === 'FAILED'
              ? `Generasi laporan GAGAL — tidak ada data ditampilkan (tidak ada sukses palsu). ${freshnessNote ?? ''}`
              : `Data BELUM FINAL (${STATUS_LABEL[runStatus]}) — ${freshnessNote ?? 'periode akuntansi terkait masih terbuka.'}`
          }
        />
      )}
      {freshness && freshness !== 'FINAL' && runStatus !== 'FAILED' && (
        <HintNote text={`Status kesegaran saat ini: ${freshness}${freshness === 'STALE' ? ' — periode sudah ditutup sejak snapshot ini dibuat.' : ''}`} />
      )}

      {comparison && reconciliation && (
        <SectionCard title="Perbandingan & Rekonsiliasi Antar Cabang" style={{ marginBottom: 16 }}>
          <p style={{ fontSize: 11, color: 'var(--alex-text-muted)', marginTop: 0, marginBottom: 10 }}>
            Total tiap cabang, jumlahnya, dan nilai konsolidasi — dihitung dengan definisi metrik/versi formula dan periode yang sama. Metrik yang secara matematis tidak valid dijumlahkan (mis. rata-rata) ditandai "Tidak dapat dijumlahkan", bukan ditotal secara keliru.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Metrik</th>
                  {reconciliation.branchCodes.map((bc) => (
                    <th key={bc} style={thStyle}>
                      {bc}
                    </th>
                  ))}
                  <th style={thStyle}>Jumlah Cabang</th>
                  <th style={thStyle}>Konsolidasi</th>
                  <th style={thStyle}>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.from(reconciliation.byMetric.entries()).map(([code, row]) => {
                  const def = metricByCode.get(code);
                  const isAdditive = ADDITIVE_FORMULA_TYPES.has((def?.formula_definition?.type as string) ?? '');
                  const sum = reconciliation.branchCodes.reduce((acc, bc) => acc + (row.perBranch.get(bc) ?? 0), 0);
                  const matches = isAdditive && Math.abs(sum - row.consolidated) < 0.0001;
                  return (
                    <tr key={code}>
                      <td style={tdStyle}>{def?.name ?? code}</td>
                      {reconciliation.branchCodes.map((bc) => (
                        <td key={bc} style={tdStyle}>
                          {formatMetricValue(row.perBranch.get(bc) ?? 0, def?.unit)}
                        </td>
                      ))}
                      <td style={tdStyle}>{isAdditive ? formatMetricValue(sum, def?.unit) : '—'}</td>
                      <td style={tdStyle}>{formatMetricValue(row.consolidated, def?.unit)}</td>
                      <td style={tdStyle}>
                        {isAdditive ? (
                          <span style={{ color: matches ? 'var(--alex-success)' : 'var(--alex-danger)', fontWeight: 700 }}>
                            {matches ? '✓ Sesuai' : '✗ Tidak Sesuai'}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--alex-text-faint)' }}>Tidak dapat dijumlahkan</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      {loading && <LoadingNote text="Menghasilkan laporan..." />}

      {!loading && reportRunId && snapshots.length === 0 && runStatus !== 'FAILED' && (
        <EmptyState text="Tidak ada metrik ACTIVE terdaftar di metric_registry untuk tenant ini." variant="section" />
      )}

      {!loading && pinnedMetrics.length > 0 && snapshots.some((s) => pinnedMetrics.includes(s.metric_code)) && (
        <div style={{ marginBottom: 20 }}>
          <GroupLabel>📌 Dipin</GroupLabel>
          <div style={{ display: 'flex', gap: 'var(--alex-card-gap)', flexWrap: 'wrap' }}>
            {snapshots
              .filter((s) => pinnedMetrics.includes(s.metric_code))
              .map((snap) => (
                <PinnableKpiCard
                  key={`pinned-${snap.snapshot_id}`}
                  snap={snap}
                  def={metricByCode.get(snap.metric_code)}
                  pinned
                  busy={pinBusy === snap.metric_code}
                  onOpen={() => setSelectedSnapshot(snap)}
                  onTogglePin={() => void togglePin(snap.metric_code)}
                />
              ))}
          </div>
        </div>
      )}

      {!loading &&
        Array.from(grouped.entries()).map(([domain, snaps]) => (
          <div key={domain} style={{ marginBottom: 20 }}>
            <GroupLabel>{DOMAIN_LABEL[domain] ?? domain}</GroupLabel>
            <div style={{ display: 'flex', gap: 'var(--alex-card-gap)', flexWrap: 'wrap' }}>
              {snaps.map((snap) => (
                <PinnableKpiCard
                  key={snap.snapshot_id}
                  snap={snap}
                  def={metricByCode.get(snap.metric_code)}
                  pinned={pinnedMetrics.includes(snap.metric_code)}
                  busy={pinBusy === snap.metric_code}
                  onOpen={() => setSelectedSnapshot(snap)}
                  onTogglePin={() => void togglePin(snap.metric_code)}
                />
              ))}
            </div>
          </div>
        ))}

      <MetricDrillDrawer
        snapshot={selectedSnapshot}
        metric={selectedSnapshot ? metricByCode.get(selectedSnapshot.metric_code) : undefined}
        open={!!selectedSnapshot}
        onClose={() => setSelectedSnapshot(null)}
        periodStart={date}
        periodEnd={date}
        branchId={scope === CONSOLIDATED ? null : scope}
      />
    </div>
  );
}

function formatMetricValue(value: number, unit?: string): string {
  if (unit === 'IDR') return `Rp ${value.toLocaleString('id-ID')}`;
  if (unit === 'RATING') return value.toFixed(2);
  return value.toLocaleString('id-ID');
}

/**
 * KPICard plus a pin/unpin toggle backed by dashboard_preferences
 * (presentation only — never affects which metric is computed, its
 * formula, or branch/tenant authorization; see fn_set_dashboard_preference_
 * checked, which always writes to the caller's own row only).
 */
function PinnableKpiCard({
  snap,
  def,
  pinned,
  busy,
  onOpen,
  onTogglePin,
}: {
  snap: ReportSnapshot;
  def: MetricDefinition | undefined;
  pinned: boolean;
  busy: boolean;
  onOpen: () => void;
  onTogglePin: () => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 168px', minWidth: 168 }}>
      <KPICard
        label={def?.name ?? snap.metric_code}
        value={formatMetricValue(snap.computed_value, def?.unit)}
        hint={def?.unit ?? undefined}
        linkLabel="Rincian"
        onClick={onOpen}
      />
      <button
        onClick={onTogglePin}
        disabled={busy}
        style={{
          alignSelf: 'flex-start',
          border: 'none',
          background: 'none',
          padding: '0 2px',
          fontSize: 10.5,
          fontWeight: 600,
          color: pinned ? 'var(--alex-primary)' : 'var(--alex-text-faint)',
          cursor: busy ? 'default' : 'pointer',
          opacity: busy ? 0.6 : 1,
        }}
      >
        {pinned ? '📌 Lepas pin' : '☆ Pin'}
      </button>
    </div>
  );
}

function MetricDrillDrawer({
  snapshot,
  metric,
  open,
  onClose,
  periodStart,
  periodEnd,
  branchId,
}: {
  snapshot: ReportSnapshot | null;
  metric: MetricDefinition | undefined;
  open: boolean;
  onClose: () => void;
  periodStart: string;
  periodEnd: string;
  branchId: string | null;
}) {
  const [reverify, setReverify] = useState<{ computed_value: number; drill_down: Array<Record<string, unknown>> } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!snapshot) return null;

  const doReverify = async () => {
    setBusy(true);
    setError(null);
    try {
      setReverify(await computeMetricValue(snapshot.metric_id, periodStart, periodEnd, branchId));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const drillRows = snapshot.drill_down ?? [];
  const columns = drillRows.length > 0 ? Object.keys(drillRows[0]) : [];

  return (
    <Drawer open={open} onClose={onClose} title={metric?.name ?? snapshot.metric_code}>
      <DrawerField label="Nilai Total (KPI)" value={formatMetricValue(snapshot.computed_value, metric?.unit)} />
      <DrawerField label="Versi Formula" value={metric ? `v${metric.version} — ${metric.formula_definition?.type ?? ''}` : '—'} />
      <DrawerField label="Dihitung Pada" value={formatDateTime(snapshot.computed_at)} />
      <HintNote text="Total di atas dan rincian di bawah dihitung dari query yang SAMA dalam satu pemanggilan (reconciliation-by-construction) — total tidak mungkin menyimpang secara struktural dari detail." />

      <div style={{ marginTop: 14 }}>
        <GroupLabel>Rincian (Drill-down) — {drillRows.length} baris</GroupLabel>
        {drillRows.length === 0 && <EmptyState text="Tidak ada baris sumber untuk periode ini (nilai 0 adalah hasil terukur nyata)." variant="section" />}
        {drillRows.length > 0 && (
          <div style={{ overflowX: 'auto', maxHeight: 260, overflowY: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c} style={thStyle}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {drillRows.slice(0, 200).map((row, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c} style={{ ...tdStyle, fontSize: 11.5 }}>
                        {String((row as Record<string, unknown>)[c] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--alex-border-soft)' }}>
        <Button variant="secondary" loading={busy} onClick={() => void doReverify()}>
          Hitung Ulang Sekarang (Verifikasi Live)
        </Button>
        {reverify && (
          <p style={{ fontSize: 11.5, color: 'var(--alex-text-muted)', marginTop: 8 }}>
            Hasil hitung ulang langsung dari sumber saat ini: <b>{formatMetricValue(reverify.computed_value, metric?.unit)}</b>
            {reverify.computed_value !== snapshot.computed_value && ' — berbeda dari snapshot karena data sumber telah berubah sejak laporan ini dibuat (perilaku yang diharapkan, snapshot tetap tidak berubah/immutable).'}
          </p>
        )}
        {error && <ErrorNote text={error} />}
      </div>
    </Drawer>
  );
}
